import React, { useEffect, useState } from "react";
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  InputLabel,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from "@material-ui/core";
import { useTranslation } from "react-i18next";
import Skeleton from "@material-ui/lab/Skeleton";
import { RepLISlider, RepLIProviderTextField } from "../../../components";
import {
  AppToggleButtonGroup,
  AppToggleButton,
} from "../../../pages/mobile/profiler/InputComponents";
import {
  races,
  sexualPreference,
  genderIdentities,
  relationshipStatuses,
  menopauseStatus,
  menopauseReasons,
  menopauseSymptons,
  department
} from "../../../data/db";
import { AppSelect } from "./ProviderInputComponents";
import {
  arrayOfNumbers,
  arrayOfPeriodAges,
} from "../../../services/util.service";
import useStyles from "./Common.style";

const PatientGeneralInformationForm = (props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const skeletonHeight = 80;

  const [autoUpdate, setAutoUpdate] = useState(false);

  const currentAge =
    new Date(Date.now()).getFullYear() -
    new Date(props.dateOfBirth).getFullYear();
  var isPeriodEligibleAge = currentAge < 8 ? false : true;

  const isRaceChecked = (race) => {
    return props.selectedRaces.includes(race);
  };

  const isSexualPreferenceChecked = (preference) => {
    return props.selectedSexualPreferences.includes(preference);
  };

  const isMenopauseSymptonsChecked = (sympton) => {
    return props.selectedMenopauseSymptons.includes(sympton);
  };

  useEffect(() => {
    if (props.submitted) props.validator.showMessages();
    props.validator.purgeFields();
    setAutoUpdate(!autoUpdate);
  }, [props.submitted, props.validator, props.desiredChildren, props.plannedChildBearingYears, props.childrenPlan, props.thoughtAboutDesChildren]);

  return (
    <Grid container spacing={1} className={classes.questionnaireContainer}>
      <Grid item xs={12}>
        {props.loading ? (
          <Skeleton height={skeletonHeight} />
        ) : (
          <RepLIProviderTextField
            label={`${t("module.provider.addPatient.form.labels.birthDate")}*`}
            type="date"
            InputLabelProps={{ shrink: true }}
            error={
              props.submitted && !props.validator.fieldValid("dateOfBirth")
            }
            helperText={props.validator.message(
              "dateOfBirth",
              props.dateOfBirth,
              "required"
            )}
            onChange={props.onDateOfBirthChange}
            value={props.dateOfBirth}
            inputProps={{
              max: new Date().toISOString().split("T")[0]
          }}
          />
        )}
      </Grid>

      {/* Children Plan */}
      <Grid item xs={12}>
        {props.loading ? (
          <Skeleton height={skeletonHeight} />
        ) : (
          <>
            <Typography gutterBottom>
              {" "}
              {`${t("module.provider.addPatient.form.labels.childrenPlan")}*`}
            </Typography>
            <FormControl
              component="fieldset"
              error={
                props.submitted && !props.validator.fieldValid("childrenPlan")
              }
            >
              <RadioGroup
                row
                aria-label="Children Plan"
                value={props.childrenPlan}
                onChange={props.onChildrenPlanChange}
              >
                {["Yes", "No", "Unsure"].map((answer, i) => (
                  <FormControlLabel
                    key={i}
                    value={answer}
                    control={<Radio color="primary" />}
                    label={answer}
                  />
                ))}
              </RadioGroup>
              <FormHelperText style={{ color: "red" }}>
                {props.validator.message(
                  "childrenPlan",
                  props.childrenPlan,
                  "required"
                )}
              </FormHelperText>
            </FormControl>
          </>
        )}
      </Grid>

      {/* Planned Child Bearing Years */}

      <Grid item xs={12}>
        {props.childrenPlan && props.childrenPlan !== "No" && (
          <Box className={classes.inputSection}>
            <Typography gutterBottom>
              {t(
                "module.provider.addPatient.form.labels.plannedChildBearingYears"
              )}
              {
                <span
                  className={
                    props.submitted &&
                      !props.validator.fieldValid("plannedChildBearingYears")
                      ? `${classes.asteriskError}`
                      : `${classes.asteriskNormal}`
                  }
                >
                  *
                </span>
              }
            </Typography>
            <FormControl
              component="fieldset"
              error={
                props.submitted &&
                !props.validator.fieldValid("plannedChildBearingYears")
              }
            >
              <RadioGroup
                row
                aria-label="Planned Child Bearing Years"
                value={props.plannedChildBearingYears}
                onChange={props.handlePlannedChildBearingYears}
              >
                {["Yes", "No"].map((answer, i) => (
                  <FormControlLabel
                    key={i}
                    value={answer}
                    control={<Radio color="primary" />}
                    label={answer}
                  />
                ))}
              </RadioGroup>
              <FormHelperText style={{ color: "red" }}>
                {props.validator.message(
                  "plannedChildBearingYears",
                  props.plannedChildBearingYears,
                  "required"
                )}
              </FormHelperText>
            </FormControl>
          </Box>
        )}
      </Grid>

      {
        /* Chilbearing Years */
        props.plannedChildBearingYears &&
        props.plannedChildBearingYears !== "No" && (
          <Grid item xs={8}>
            {props.loading ? (
              <Skeleton height={skeletonHeight} />
            ) : (
              <>
                <Typography gutterBottom>
                  {t(
                    "module.provider.addPatient.form.labels.childbearingYearFrame"
                  )}
                </Typography>
                <RepLISlider
                  value={props.childbearingYearFrame}
                  onChange={props.onChildbearingYearFrameChange}
                  valueLabelDisplay="auto"
                  min={14}
                  max={50}
                />
              </>
            )}
          </Grid>
        )
      }

      <Grid item xs={12}>
        {props.loading ? (
          <Skeleton height={skeletonHeight} />
        ) : (
          <RepLIProviderTextField
            label={`${t(
              "module.provider.addPatient.form.labels.currentChildrenNum"
            )}*`}
            error={
              props.submitted && !props.validator.fieldValid("currentChildren")
            }
            helperText={props.validator.message(
              "currentChildren",
              props.currentChildren,
              "required|integer|min:0,num|max:10,num"
            )}
            onChange={props.onCurrentChildrenChange}
            min="0"
            max="10"
            value={props.currentChildren && Math.max(0, props.currentChildren)}
            type="number"
          />
        )}
      </Grid>

      <Grid item xs={12}>
        {props.loading ? (
          <Skeleton height={skeletonHeight} />
        ) : (
          <>
            <Typography gutterBottom>{`${t(
              "module.provider.addPatient.form.labels.thoughtAboutDesChildren"
            )}*`}</Typography>
            <FormControl
              component="fieldset"
              error={
                props.submitted && !props.validator.fieldValid("thoughtAboutDesChildren")
              }
            >
              <RadioGroup
                row
                aria-label="Thought About Desired Children"
                value={props.thoughtAboutDesChildren}
                onChange={props.handleThoughtAboutDesChildren}
              >
                {["Yes", "No", "Undecided"].map((answer, i) => (
                  <FormControlLabel
                    key={i}
                    value={answer}
                    control={<Radio color="primary" />}
                    label={answer}
                  />
                ))}
              </RadioGroup>
              <FormHelperText style={{ color: "red" }}>
                {props.validator.message(
                  "thoughtAboutDesChildren",
                  props.thoughtAboutDesChildren,
                  "required"
                )}
              </FormHelperText>
            </FormControl>
          </>
        )}
      </Grid>

      {props.thoughtAboutDesChildren &&
        props.thoughtAboutDesChildren === "Yes" && (
          <Grid item xs={12}>
            {props.loading ? (
              <Skeleton height={skeletonHeight} />
            ) : (
              <RepLIProviderTextField
                label={`${t(
                  "module.provider.addPatient.form.labels.desiredChildrenNum"
                )}*`}
                error={
                  props.submitted &&
                  !props.validator.fieldValid("desiredChildren")
                }
                helperText={props.validator.message(
                  "desiredChildren",
                  props.desiredChildren,
                  "required|integer|min:0,num|max:10,num"
                )}
                onChange={props.onDesiredChildrenChange}
                min="0"
                max="10"
                value={
                  props.desiredChildren && Math.max(0, props.desiredChildren)
                }
                type="number"
              />
            )}
          </Grid>
        )}

      <Grid item xs={6}>
        {props.loading ? (
          <Skeleton height={skeletonHeight} />
        ) : (
          <AppSelect
            id="firstPeriodAge"
            label={`${t(
              "module.provider.addPatient.form.labels.firstPeriodAge"
            )}*`}
            value={props.firstPeriodAge}
            title={props.firstPeriodAge}
            items={[
              "",
              "I haven't started my first period",
              ...arrayOfPeriodAges(currentAge, isPeriodEligibleAge),
            ]}
            fullWidth
            margin="normal"
            onChange={props.onFirstPeriodAgeChange}
            error={
              props.submitted && !props.validator.fieldValid("firstPeriodAge")
            }
            helperText={props.validator.message(
              "firstPeriodAge",
              props.firstPeriodAge,
              "required"
            )}
          />
        )}
      </Grid>
      <Grid item xs={6}>
        {props.loading ? (
          <Skeleton height={skeletonHeight} />
        ) : (
          <AppSelect
            id="sexuallyActive"
            label={`${t(
              "module.provider.addPatient.form.labels.sexualDebutAge"
            )}*`}
            value={props.sexualDebutAge}
            items={[
              "",
              "I am not yet sexually active",
              ...arrayOfNumbers(10, currentAge - 9),
            ]}
            title={props.sexualDebutAge}
            fullWidth
            margin="normal"
            onChange={props.onSexualDebutAgeChange}
            error={
              props.submitted && !props.validator.fieldValid("sexualDebutAge")
            }
            helperText={props.validator.message(
              "sexualDebutAge",
              props.sexualDebutAge,
              "required"
            )}
          />
        )}
      </Grid>

      {/* Organization */}
      <Grid item xs={6}>
        {props.userRoles && props.userRoles.length > 1 ? (
          props.loading ? (
            <Skeleton height={skeletonHeight} />
          ) : (
            <FormControl
              id="organization"
              fullWidth
              variant="outlined"
              style={{ margin: "16px 0 8px 0" }}
            >
              <InputLabel>Organization</InputLabel>
              <Select
                label="Organization"
                multiple
                value={props.patientOrg}
                onChange={props.onPatientOrgChange}
                inputProps={{
                  name: "organization",
                  id: "organization",
                }}
                renderValue={(selected) => selected.join(", ")}
              //   MenuProps={MenuProps}
              >
                {props.providerOrgs &&
                  props.providerOrgs.map((org, i) => (
                    <MenuItem key={i} value={org}>
                      <ListItemIcon>
                        <Checkbox
                          color="primary"
                          checked={props.patientOrg.indexOf(org) > -1}
                        />
                      </ListItemIcon>
                      <ListItemText primary={org} />
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          )
        ) : props.loading ? (
          <Skeleton height={skeletonHeight} />
        ) : (
          <AppSelect
            id="patientOrg"
            label={`${t("module.provider.addPatient.form.labels.patientOrg")}*`}
            value={props.patientOrg}
            items={props.providerOrgs}
            title={props.patientOrg}
            fullWidth
            margin="normal"
            onChange={props.onPatientOrgChange}
            error={props.submitted && !props.validator.fieldValid("patientOrg")}
            helperText={props.validator.message(
              "patientOrg",
              props.patientOrg,
              "required"
            )}
          />
        )}
      </Grid>
      {/* Department */}
      <Grid item xs={6}>
        {props.userRoles && props.userRoles.length > 1 ? (
          props.loading ? (
            <Skeleton height={skeletonHeight} />
          ) : (
            <FormControl
              id="department"
              fullWidth
              variant="outlined"
              style={{ margin: "16px 0 8px 0" }}
            >
              <InputLabel>Department</InputLabel>
              <Select
                label="Department"
                value={props.department}
                onChange={props.onDepartmentChange}
                inputProps={{
                  name: "department",
                  id: "department",
                }}
              >
                {department &&
                  department.map((dept, i) => (
                    <MenuItem key={i} value={dept}>
                      <ListItemIcon>
                        <Checkbox
                          color="primary"
                          checked={props.department.indexOf(dept) > -1}
                        />
                      </ListItemIcon>
                      <ListItemText primary={dept} />
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          )
        ) : props.loading ? (
          <Skeleton height={skeletonHeight} />
        ) : (
          <AppSelect
            id="department"
            label={`${t("module.provider.addPatient.form.labels.department")}*`}
            value={props.department}
            items={department}
            title={props.department}
            fullWidth
            margin="normal"
            onChange={props.onDepartmentChange}
            error={props.submitted && !props.validator.fieldValid("department")}
            helperText={props.validator.message(
              "department",
              props.department,
              "required"
            )}
          />
        )}
      </Grid>

      {/* Menopause */}
      <Grid item xs={12}>
        {props.loading ? (
          <Skeleton height={skeletonHeight} />
        ) : (
          <>
            <Typography gutterBottom>{`${t(
              "module.provider.addPatient.form.labels.menopause"
            )}*`}</Typography>
            <FormControl
              component="fieldset"
              error={
                props.submitted && !props.validator.fieldValid("menopause")
              }
            >
              <RadioGroup
                row
                aria-label="Menopause"
                value={props.menopause}
                onChange={props.onMenopauseChange}
              >
                {["Yes", "No", "Unsure"].map((answer, i) => (
                  <FormControlLabel
                    key={i}
                    value={answer}
                    control={<Radio color="primary" />}
                    label={answer}
                  />
                ))}
              </RadioGroup>
              <FormHelperText>
                {props.validator.message(
                  "menopause",
                  props.menopause,
                  "required"
                )}
              </FormHelperText>
            </FormControl>
          </>
        )}
      </Grid>

      <Grid item xs={12}>
        {/* Menopause YES */}
        <Box display={props.menopause === "Yes" ? "block" : "none"}>
          {/* Menopause Age */}
          <Grid item xs={12}>
            <Typography gutterBottom>{`${t(
              "module.provider.addPatient.form.labels.menopauseAgeBegins"
            )}*`}</Typography>
            <AppSelect
              id="menopauseAge"
              label="Age"
              value={props.menopauseAge}
              items={[
                ...arrayOfNumbers(
                  parseInt(props.firstPeriodAge),
                  currentAge - parseInt(props.firstPeriodAge)
                ),
              ]}
              fullWidth
              margin="normal"
              onChange={props.onMenopauseAgeChange}
              error={
                props.submitted && !props.validator.fieldValid("menopauseAge")
              }
              helperText={props.validator.message(
                "menopauseAge",
                props.menopauseAge,
                `menopause_field:${props.menopause}`
              )}
            />
          </Grid>
          {/* Menopause Status */}
          <Grid item xs={12}>
            <Typography gutterBottom>{`${t(
              "module.provider.addPatient.form.labels.menopauseStatus"
            )}*`}</Typography>
            <AppSelect
              id="menopauseStatus"
              label="Status"
              value={props.menopauseStatus}
              items={menopauseStatus}
              fullWidth
              margin="normal"
              onChange={props.onMenopauseStatusChange}
              error={
                props.submitted &&
                !props.validator.fieldValid("menopauseStatus")
              }
              helperText={props.validator.message(
                "menopauseStatus",
                props.menopauseStatus,
                `menopause_field:${props.menopause}`
              )}
            />
          </Grid>
          {/* Menopause Reason */}
          <Grid item xs={12}>
            <Typography gutterBottom>{`${t(
              "module.provider.addPatient.form.labels.menopauseReason"
            )}*`}</Typography>
            <AppSelect
              id="menopauseReason"
              label="Reason"
              value={props.menopauseReason}
              items={menopauseReasons}
              fullWidth
              margin="normal"
              onChange={props.onMenopauseReasonChange}
              error={
                props.submitted &&
                !props.validator.fieldValid("menopauseReason")
              }
              helperText={props.validator.message(
                "menopauseReason",
                props.menopauseReason,
                `menopause_field:${props.menopause}`
              )}
            />
          </Grid>
        </Box>

        {/* Menopause Unsure */}

        <Box display={props.menopause === "Unsure" ? "block" : "none"}>
          {/* MenopauseSymptons */}
          <Box className={classes.inputSection}>
            <FormControl
              component="fieldset"
              error={
                props.submitted &&
                !props.validator.fieldValid("menopauseSymptons")
              }
            >
              <Typography gutterBottom>
                What are your top three symptoms?
              </Typography>
              <AppToggleButtonGroup>
                {menopauseSymptons.map((symptons) => (
                  <AppToggleButton
                    key={symptons}
                    selected={isMenopauseSymptonsChecked(symptons)}
                    onChange={() => props.onMenopauseSymptonChanges(symptons)}
                    name={symptons}
                    error={
                      props.submitted &&
                      !props.validator.fieldValid("selectedMenopauseSymptons")
                    }
                    label={symptons}
                  />
                ))}
              </AppToggleButtonGroup>
              <FormHelperText>
                {props.validator.message(
                  "selectedMenopauseSymptons",
                  props.selectedMenopauseSymptons,
                  `menopause_unsure:${props.menopause}`
                )}
              </FormHelperText>
            </FormControl>
          </Box>
          {/* Menopause Age Begins */}
          <Grid item xs={12}>
            <Typography gutterBottom>
              What age did symptoms start for you?
            </Typography>
            <AppSelect
              id="menopauseSymptonAge"
              label="Age Begins"
              value={props.menopauseSymptonAge}
              items={[
                ...arrayOfNumbers(
                  parseInt(props.firstPeriodAge),
                  currentAge - parseInt(props.firstPeriodAge)
                ),
              ]}
              fullWidth
              margin="normal"
              onChange={props.onMenopauseSymptonAgeChange}
              error={
                props.submitted &&
                !props.validator.fieldValid("menopauseSymptonAge")
              }
              helperText={props.validator.message(
                "menopauseSymptonAge",
                props.menopauseSymptonAge,
                `menopause_unsure_age:${props.menopause}`
              )}
            />
          </Grid>
        </Box>
      </Grid>

      {/* Race/Ethnicity */}
      <Grid item xs={12}>
        {props.loading ? (
          <Skeleton height={skeletonHeight} />
        ) : (
          <Box>
            <FormControl
              component="fieldset"
              error={props.submitted && !props.validator.fieldValid("races")}
            >
              <FormLabel className={classes.formLabelStyle}>
                {`${t(
                  "module.provider.addPatient.form.labels.raceEthnicity"
                )}*`}
              </FormLabel>
              <AppToggleButtonGroup>
                {races.map((race) => (
                  <AppToggleButton
                    key={race}
                    selected={isRaceChecked(race)}
                    onChange={() => props.onSelectedRacesChange(race)}
                    name={race}
                    label={race}
                  />
                ))}
              </AppToggleButtonGroup>
              <FormHelperText>
                {props.validator.message(
                  "races",
                  props.selectedRaces,
                  "required"
                )}
              </FormHelperText>
            </FormControl>
            <Box
              display={
                isRaceChecked("Prefer to self-describe") ? "block" : "none"
              }
            >
              <TextField
                variant="outlined"
                label="Self-describe*"
                margin="normal"
                fullWidth
                inputProps={{ maxLength: 2500 }}
                value={props.raceSelfDescribe}
                onChange={(e) =>
                  props.onRaceSelfDescribeChange(e, "Race Self Describe")
                }
                error={
                  props.submitted &&
                  !props.validator.fieldValid("raceSelfDescribe")
                }
                helperText={props.validator.message(
                  "raceSelfDescribe",
                  props.raceSelfDescribe,
                  `race_self_describe:${props.selectedRaces}`
                )}
              />
            </Box>
          </Box>
        )}
      </Grid>
      {/* Sex Assigned at Birth */}
      <Grid item xs={12}>
        {props.loading ? (
          <Skeleton height={skeletonHeight} />
        ) : (
          <FormControl
            component="fieldset"
            error={props.submitted && !props.validator.fieldValid("gender")}
          >
            <FormLabel className={classes.formLabelStyle}>
              {`${t(
                "module.provider.addPatient.form.labels.sexualPreference"
              )}*`}
            </FormLabel>
            <RadioGroup
              aria-label="Sexual Preference"
              value={props.gender}
              row
              onChange={props.onGenderChange}
            >
              <FormControlLabel
                value="male"
                control={<Radio color="primary" />}
                label={t("common.substantive.male")}
              />
              <FormControlLabel
                value="female"
                control={<Radio color="primary" />}
                label={t("common.substantive.female")}
              />
              <FormControlLabel
                value="intersex"
                control={<Radio color="primary" />}
                label={t("common.substantive.intersex")}
              />
              <FormControlLabel
                value="preferNotToAnswer"
                control={<Radio color="primary" />}
                label={t("common.substantive.PNA")}
              />
            </RadioGroup>
            <FormHelperText>
              {props.validator.message("gender", props.gender, "required")}
            </FormHelperText>
          </FormControl>
        )}
      </Grid>
      {/* Gender Identity */}
      <Grid item xs={12}>
        {props.loading ? (
          <Skeleton height={skeletonHeight} />
        ) : (
          <Box>
            <FormControl
              component="fieldset"
              error={
                props.submitted && !props.validator.fieldValid("genderIdentity")
              }
            >
              <FormLabel className={classes.formLabelStyle}>
                {`${t(
                  "module.provider.addPatient.form.labels.genderIdentity"
                )}*`}
              </FormLabel>
              <RadioGroup
                row
                aria-label="Gender Identity"
                value={props.genderIdentity}
                onChange={props.onGenderIdentityChange}
              >
                {genderIdentities.map((identity, i) => (
                  <FormControlLabel
                    key={i}
                    value={identity}
                    control={<Radio color="primary" />}
                    label={identity}
                  />
                ))}
              </RadioGroup>
              <FormHelperText>
                {props.validator.message(
                  "genderIdentity",
                  props.genderIdentity,
                  "required"
                )}
              </FormHelperText>
            </FormControl>
            <Box
              display={
                props.genderIdentity === "Gender identity not listed"
                  ? "block"
                  : "none"
              }
            >
              <TextField
                variant="outlined"
                label="Please describe your gender identity*"
                margin="normal"
                fullWidth
                inputProps={{ maxLength: 2500 }}
                value={props.genderIdentitySelfDescribe}
                onChange={(e) =>
                  props.onGenderIdentitySelfDescribeChange(
                    e,
                    "Gender Identity Self Describe"
                  )
                }
                error={
                  props.submitted &&
                  !props.validator.fieldValid("genderIdentitySelfDescribe")
                }
                helperText={props.validator.message(
                  "genderIdentitySelfDescribe",
                  props.genderIdentitySelfDescribe,
                  `gender_identity_self_describe:${props.genderIdentity}`
                )}
              />
            </Box>
          </Box>
        )}
      </Grid>
      {/* Sexual Orientation */}
      <Grid item xs={12}>
        {props.loading ? (
          <Skeleton height={skeletonHeight} />
        ) : (
          <Box>
            <FormControl
              component="fieldset"
              error={
                props.submitted &&
                !props.validator.fieldValid("sexualPreferences")
              }
            >
              <FormLabel className={classes.formLabelStyle}>
                {`${t(
                  "module.provider.addPatient.form.labels.sexualPreferences"
                )}*`}
              </FormLabel>
              <AppToggleButtonGroup>
                {sexualPreference.map((preference) => (
                  <AppToggleButton
                    key={preference}
                    selected={isSexualPreferenceChecked(preference)}
                    onChange={() => props.onSexualPreferenceChange(preference)}
                    name={preference}
                    label={preference}
                  />
                ))}
              </AppToggleButtonGroup>
              <FormHelperText>
                {props.validator.message(
                  "sexualPreferences",
                  props.selectedSexualPreferences,
                  "required"
                )}
              </FormHelperText>
            </FormControl>
            <Box
              display={
                isSexualPreferenceChecked("Prefer to self-describe")
                  ? "block"
                  : "none"
              }
            >
              <TextField
                variant="outlined"
                label="Self-describe*"
                margin="normal"
                fullWidth
                inputProps={{ maxLength: 2500 }}
                value={props.sexualPreferenceSelfDescribe}
                onChange={(e) =>
                  props.onSexualPreferenceSelfDescribeChange(
                    e,
                    "Sexual Preference Self Describe"
                  )
                }
                error={
                  props.submitted &&
                  !props.validator.fieldValid("sexualPreferenceSelfDescribe")
                }
                helperText={props.validator.message(
                  "sexualPreferenceSelfDescribe",
                  props.sexualPreferenceSelfDescribe,
                  `sexual_preference_self_describe:${props.selectedSexualPreferences}`
                )}
              />
            </Box>
          </Box>
        )}
      </Grid>
      {/* Relation Status */}
      <Grid item xs={12}>
        {props.loading ? (
          <Skeleton height={skeletonHeight} />
        ) : (
          <FormControl
            component="fieldset"
            error={
              props.submitted &&
              !props.validator.fieldValid("relationshipStatus")
            }
          >
            <FormLabel className={classes.formLabelStyle}>
              {`${t(
                "module.provider.addPatient.form.labels.relationshipStatus"
              )}*`}
            </FormLabel>
            <RadioGroup
              row
              aria-label="Relationship Status"
              value={props.relationshipStatus}
              onChange={props.onRelationshipStatusChange}
            >
              {relationshipStatuses.map((status, i) => (
                <FormControlLabel
                  key={i}
                  value={status}
                  control={<Radio color="primary" />}
                  label={status}
                />
              ))}
            </RadioGroup>
            <FormHelperText>
              {props.validator.message(
                "relationshipStatus",
                props.relationshipStatus,
                "required"
              )}
            </FormHelperText>
          </FormControl>
        )}
      </Grid>


    </Grid>
  );
};

export default PatientGeneralInformationForm;
